import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { Row, Col, Card, Button, Form } from "react-bootstrap";
import { db, auth } from "../firebaseConfig";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { Link } from "react-router-dom"; // Ensure Link is imported

export default function Profile() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    emergencyInfo: "",
    otherInfo: "",
  });
  const [error, setError] = useState("");
  const [profilePic, setProfilePic] = useState(null); // Holds the current profile picture

  // Monitor auth state and fetch user data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);

        // Fetch user data from Firestore
        try {
          const userDocRef = doc(db, "Users", currentUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData(data);
            setFormData({
              fullName: data.fullName || "",
              email: data.email || "",
              emergencyInfo: data.emergencyInfo || "",
              otherInfo: data.otherInfo || "",
            });
            setProfilePic(data.profilePicture || null);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Validate the form data
  const validateForm = () => {
    if (!formData.email.trim()) return "Email is required.";
    if (!formData.fullName.trim()) return "Full Name is required.";
    return null;
  };

  // Save updated data to Firestore
  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!user) {
      setError("No user is logged in.");
      return;
    }

    try {
      const userDocRef = doc(db, "Users", user.uid);
      await updateDoc(userDocRef, {
        fullName: formData.fullName,
        email: formData.email,
        emergencyInfo: formData.emergencyInfo,
        otherInfo: formData.otherInfo,
      });
      setUserData(formData); // Update local state
      setEditMode(false);
      setError(""); // Clear any previous errors
    } catch (error) {
      console.error("Error updating user data:", error);
      setError("Failed to update user data. Please try again.");
    }
  };

  // Handle profile picture upload
  const handleUploadPicture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result.split(",")[1]; // Get Base64 encoded string
      try {
        const userDocRef = doc(db, "Users", user.uid);
        await updateDoc(userDocRef, { profilePicture: base64String });
        setProfilePic(base64String); // Update local state
      } catch (error) {
        console.error("Error uploading profile picture:", error);
      }
    };
    reader.readAsDataURL(file); // Read file as data URL
  };

  if (!user) {
    return <p>Loading user information...</p>;
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-user-settings-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">User Profile</h1>
          </div>
          <p className="lead text-muted">Manage your personal information and account settings</p>
        </div>

        <Row className="g-5">
          {/* Enhanced Profile Overview */}
          <Col xl={4}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="bg-gradient-primary text-white p-4 text-center" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="position-relative d-inline-block">
                  <img
                    src={
                      profilePic
                        ? `data:image/jpeg;base64,${profilePic}`
                        : "https://via.placeholder.com/150"
                    }
                    alt="User Avatar"
                    className="img-fluid rounded-circle shadow-lg"
                    style={{ width: "120px", height: "120px", objectFit: "cover" }}
                  />
                  <div className="position-absolute bottom-0 end-0">
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      id="upload-picture"
                      onChange={handleUploadPicture}
                    />
                    <Button
                      variant="light"
                      size="sm"
                      className="rounded-circle p-2 shadow"
                      onClick={() => document.getElementById("upload-picture").click()}
                    >
                      <i className="ri-camera-line"></i>
                    </Button>
                  </div>
                </div>
                <h4 className="fw-bold mt-3 mb-1">{userData?.fullName || "User"}</h4>
                <p className="mb-0 opacity-75">{userData?.email || "No email provided"}</p>
              </div>
              <Card.Body className="p-4 text-center">
                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    className="rounded-pill fw-semibold"
                    onClick={() => document.getElementById("upload-picture").click()}
                  >
                    <i className="ri-upload-cloud-2-line me-2"></i>
                    Upload New Picture
                  </Button>
                  <Link to="/changepassword">
                    <Button variant="outline-primary" className="rounded-pill fw-semibold w-100">
                      <i className="ri-lock-line me-2"></i>
                      Change Password
                    </Button>
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Enhanced Personal Information */}
          <Col xl={8}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="bg-gradient-info text-white p-4 d-flex justify-content-between align-items-center" 
                   style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-user-settings-line fs-4 me-3"></i>
                  <h5 className="mb-0 fw-bold">Personal Information</h5>
                </div>
                <div>
                  {!editMode && (
                    <Button
                      variant="light"
                      size="sm"
                      className="me-2 rounded-pill px-3 fw-semibold"
                      onClick={() => setEditMode(true)}
                    >
                      <i className="ri-edit-line me-1"></i>
                      Edit
                    </Button>
                  )}
                </div>
              </div>
              <Card.Body className="p-4">
                {editMode ? (
                  <Form>
                    <Row className="g-4">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            <i className="ri-user-line me-2"></i>Full Name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            className="form-control-lg shadow-sm border-2"
                            style={{ borderRadius: '12px' }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            <i className="ri-mail-line me-2"></i>Email
                          </Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="form-control-lg shadow-sm border-2"
                            style={{ borderRadius: '12px' }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            <i className="ri-alarm-warning-line me-2"></i>Emergency Info
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="emergencyInfo"
                            value={formData.emergencyInfo}
                            onChange={handleInputChange}
                            className="shadow-sm border-2"
                            style={{ borderRadius: '12px' }}
                            placeholder="Emergency contact information..."
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            <i className="ri-information-line me-2"></i>Other Info
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="otherInfo"
                            value={formData.otherInfo}
                            onChange={handleInputChange}
                            className="shadow-sm border-2"
                            style={{ borderRadius: '12px' }}
                            placeholder="Additional information..."
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="mt-4 d-flex gap-3">
                      <Button 
                        variant="success" 
                        onClick={handleSave} 
                        className="px-4 py-2 rounded-pill fw-semibold"
                      >
                        <i className="ri-save-line me-2"></i>
                        Save Changes
                      </Button>
                      <Button 
                        variant="outline-secondary" 
                        onClick={() => setEditMode(false)}
                        className="px-4 py-2 rounded-pill fw-semibold"
                      >
                        <i className="ri-close-line me-2"></i>
                        Cancel
                      </Button>
                    </div>
                  </Form>
                ) : (
                  <Row className="g-4">
                    <Col md={6}>
                      <Card className="border-0 shadow-sm rounded-3 h-100"
                            style={{ 
                              transition: 'all 0.3s ease',
                              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                            }}>
                        <Card.Body className="p-4">
                          <div className="d-flex align-items-center mb-3">
                            <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                              <i className="ri-mail-line fs-4 text-primary"></i>
                            </div>
                            <h6 className="fw-bold mb-0">Email Address</h6>
                          </div>
                          <p className="text-muted mb-0">{userData?.email || "Not provided"}</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="border-0 shadow-sm rounded-3 h-100"
                            style={{ 
                              transition: 'all 0.3s ease',
                              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                            }}>
                        <Card.Body className="p-4">
                          <div className="d-flex align-items-center mb-3">
                            <div className="bg-info bg-opacity-10 rounded-circle p-3 me-3">
                              <i className="ri-user-line fs-4 text-info"></i>
                            </div>
                            <h6 className="fw-bold mb-0">Full Name</h6>
                          </div>
                          <p className="text-muted mb-0">{userData?.fullName || "Not provided"}</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="border-0 shadow-sm rounded-3 h-100"
                            style={{ 
                              transition: 'all 0.3s ease',
                              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                            }}>
                        <Card.Body className="p-4">
                          <div className="d-flex align-items-center mb-3">
                            <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                              <i className="ri-alarm-warning-line fs-4 text-warning"></i>
                            </div>
                            <h6 className="fw-bold mb-0">Emergency Info</h6>
                          </div>
                          <p className="text-muted mb-0">
                            {userData?.emergencyInfo || "No emergency info provided"}
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="border-0 shadow-sm rounded-3 h-100"
                            style={{ 
                              transition: 'all 0.3s ease',
                              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                            }}>
                        <Card.Body className="p-4">
                          <div className="d-flex align-items-center mb-3">
                            <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                              <i className="ri-information-line fs-4 text-success"></i>
                            </div>
                            <h6 className="fw-bold mb-0">Other Info</h6>
                          </div>
                          <p className="text-muted mb-0">
                            {userData?.otherInfo || "No additional info provided"}
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                )}
                {error && (
                  <div className="alert alert-danger mt-4 rounded-3 border-0 shadow-sm">
                    <i className="ri-error-warning-line me-2"></i>
                    {error}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Enhanced Informational Cards */}
        <Row className="g-4 mt-5">
          <Col md={4}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-danger text-white p-3" 
                   style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-shield-check-line fs-4 me-3"></i>
                  <h6 className="mb-0 fw-bold">Security Tip</h6>
                </div>
              </div>
              <Card.Body className="p-4">
                <p className="text-muted mb-0">
                  Never share your password with anyone. Always use a secure and unique password.
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-info text-white p-3" 
                   style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-eye-line fs-4 me-3"></i>
                  <h6 className="mb-0 fw-bold">Profile Visibility</h6>
                </div>
              </div>
              <Card.Body className="p-4">
                <p className="text-muted mb-0">
                  Ensure your profile information is accurate and up-to-date for better communication.
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-primary text-white p-3" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-customer-service-2-line fs-4 me-3"></i>
                  <h6 className="mb-0 fw-bold">Contact Support</h6>
                </div>
              </div>
              <Card.Body className="p-4">
                <p className="text-muted mb-0">
                  Need help? Contact our support team at support@example.com.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
